import React from 'react';

import { useTranslation } from 'react-i18next';

import { Currency } from 'domains-billing-currency';
import { useCurrencyExpansionEnabled } from 'react-hooks';

import CardContainer from 'components/cards/CardContainer';
import Dropdown from 'components/form/Dropdown';
import { H5, M, S } from 'components/typography';

import styles from './styles.scss';

interface CurrencySettingProps {
  currency: Currency;
  updateCurrency: (currency: Currency) => void;
}

const currencyOptions = [
  {
    label: 'abp.billingAndPaymentSettings.currency.aud',
    value: Currency.AUD,
  },
  {
    label: 'abp.billingAndPaymentSettings.currency.brl',
    value: Currency.BRL,
  },
  {
    label: 'abp.billingAndPaymentSettings.currency.cad',
    value: Currency.CAD,
  },
  {
    label: 'abp.billingAndPaymentSettings.currency.eur',
    value: Currency.EUR,
  },
  {
    label: 'abp.billingAndPaymentSettings.currency.gbp',
    value: Currency.GBP,
  },
  {
    label: 'abp.billingAndPaymentSettings.currency.hkd',
    value: Currency.HKD,
  },
  {
    label: 'abp.billingAndPaymentSettings.currency.huf',
    value: Currency.HUF,
  },
  {
    label: 'abp.billingAndPaymentSettings.currency.usd',
    value: Currency.USD,
  },
  {
    label: 'abp.billingAndPaymentSettings.currency.sgd',
    value: Currency.SGD,
  },
  {
    label: 'abp.billingAndPaymentSettings.currency.inr',
    value: Currency.INR,
  },
  {
    label: 'abp.billingAndPaymentSettings.currency.thb',
    value: Currency.THB,
  },
];

const expandedCurrencyOptions = Object.values(Currency)
  .filter(
    (currency) =>
      currency !== Currency.UNKNOWN && currency !== Currency.EssensysCredits
  )
  .map((currency) => ({
    label: `abp.billingAndPaymentSettings.currency.${currency.toLowerCase()}`,
    value: currency,
  }))
  .sort((a, b) => a.value.localeCompare(b.value));


export function CurrencySetting({
  currency,
  updateCurrency,
}: CurrencySettingProps) {
  const { t } = useTranslation();

  const enabledCurrencyExpansion = useCurrencyExpansionEnabled();

  const currencyOptionsToUse = enabledCurrencyExpansion ? expandedCurrencyOptions : currencyOptions;

  return (
    <CardContainer className={styles.PaymentSettings}>
      <div className={styles.SectionHeader}>
        <H5>{t('abp.billingAndPaymentSettings.currency.header')}</H5>
        <S>{t('abp.billingAndPaymentSettings.currency.description')}</S>
      </div>

      <M mt={2} className={styles.FieldLabel}>
        <label>{t('abp.billingAndPaymentSettings.currency.label')}</label>
      </M>
      <div className={styles.CurrencyValueContainer}>
        <Dropdown
          className={styles.CurrencyDropdown}
          value={currency}
          items={currencyOptionsToUse}
          onChange={option => updateCurrency(option.value)}
          testId="currencyDropdownInput"
        />
      </div>
    </CardContainer>
  );
}

